import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolvePhoto } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Eye, Heart, X, Sparkles, Loader2 } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import { motion } from "framer-motion";

export default function AdmirersPanel({ isQuietPlus, onAction }) {
  const [admirers, setAdmirers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(!isQuietPlus);

  useEffect(() => {
    if (!isQuietPlus) {
      setGated(true);
      setLoading(false);
      return;
    }
    setGated(false);
    setLoading(true);
    api.get("/admirers")
      .then(({ data }) => setAdmirers(data))
      .catch((err) => {
        if (err?.response?.status === 402) setGated(true);
      })
      .finally(() => setLoading(false));
  }, [isQuietPlus]);

  const respond = async (userId, action) => {
    setAdmirers((prev) => prev.filter((a) => a.user.id !== userId));
    try {
      const { data } = await api.post("/swipe", { target_id: userId, action });
      if (data.match && onAction) onAction(data);
    } catch (err) {
      toast.error("Couldn't respond");
    }
  };

  if (gated) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-10 h-full" data-testid="admirers-gated">
        <div className="w-14 h-14 rounded-full bg-[#F3E8DD] border border-[#D4A373]/40 flex items-center justify-center mb-5">
          <Lock size={20} strokeWidth={1.5} className="text-[#913831]" />
        </div>
        <h3 className="font-serif text-3xl text-[#1A1818] tracking-tight">Who likes you, privately.</h3>
        <p className="text-sm text-[#737373] mt-3 max-w-xs">
          Quiet+ shows the list of people who&apos;ve already said yes. Decide on your own time.
        </p>
        <Link to="/billing" data-testid="admirers-upgrade-btn"
          className="mt-7 inline-flex items-center gap-2 bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-6 py-3 font-medium">
          <Sparkles size={14} /> Upgrade to Quiet+
        </Link>
      </div>
    );
  }

  if (loading) {
    return <div className="p-10 text-center text-[#737373]" data-testid="admirers-loading">
      <Loader2 size={20} className="mx-auto animate-spin" />
    </div>;
  }

  if (admirers.length === 0) {
    return (
      <div className="p-10 text-center text-[#737373]" data-testid="admirers-empty">
        <Eye size={20} className="mx-auto mb-3" strokeWidth={1.25} />
        <p className="font-serif text-2xl text-[#1A1818]">No admirers yet.</p>
        <p className="text-sm mt-2">Keep your profile honest — they&apos;re coming.</p>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6 overflow-y-auto" data-testid="admirers-list">
      <div className="text-xs uppercase tracking-[0.25em] text-[#737373] mb-4">
        {admirers.length} {admirers.length === 1 ? "person likes" : "people like"} you
      </div>
      <div className="grid grid-cols-2 gap-4">
        {admirers.map((a, i) => (
          <motion.div
            key={a.user.id}
            data-testid={`admirer-${a.user.id}`}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="bg-white rounded-2xl border border-[#E5DFD3] overflow-hidden shadow-[0_15px_30px_-15px_rgba(0,0,0,0.08)]"
          >
            <div className="aspect-[3/4] bg-[#F0EBE1]">
              {a.user.photos?.[0] && (
                <img src={resolvePhoto(a.user.photos[0])} alt={a.user.name}
                  className="w-full h-full object-cover" />
              )}
            </div>
            <div className="p-3">
              <div className="font-serif text-lg text-[#1A1818] flex items-center gap-1.5">
                <span>{a.user.name}, {a.user.age}</span>
                {a.user.verified && <VerifiedBadge />}
              </div>
              {a.note && (
                <div className="mt-2 text-xs italic text-[#737373] line-clamp-3 font-serif" data-testid={`admirer-note-${a.user.id}`}>
                  &ldquo;{a.note}&rdquo;
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <button data-testid={`admirer-pass-${a.user.id}`}
                  onClick={() => respond(a.user.id, "pass")}
                  className="flex-1 h-9 rounded-full border border-[#E5DFD3] hover:bg-[#F0EBE1] flex items-center justify-center">
                  <X size={14} />
                </button>
                <button data-testid={`admirer-like-${a.user.id}`}
                  onClick={() => respond(a.user.id, "like")}
                  className="flex-1 h-9 rounded-full bg-[#913831] hover:bg-[#7A2E28] text-white flex items-center justify-center">
                  <Heart size={14} />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
